import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { supabase, supabaseFunctionsUrl } from '../config/supabase';
import { sendPasswordResetEmail } from '../utils/emailJsPortal';
import './Settings.css';

const Settings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('reset-password');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Admins tab state
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminsError, setAdminsError] = useState('');
  const [sendingResetFor, setSendingResetFor] = useState(null);
  const [resetResults, setResetResults] = useState({});

  const loadAdmins = useCallback(async () => {
    setAdminsLoading(true);
    setAdminsError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${supabaseFunctionsUrl}/get-admins`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.admins) {
        setAdmins(json.admins);
      } else {
        setAdminsError(json.error || 'Failed to load admins');
      }
    } catch (err) {
      setAdminsError(err?.message || 'Failed to load admins');
    } finally {
      setAdminsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'admins') {
      loadAdmins();
    }
  }, [activeTab, loadAdmins]);

  const handleSendAdminReset = async (adminEmail) => {
    setSendingResetFor(adminEmail);
    setResetResults((prev) => ({ ...prev, [adminEmail]: null }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const redirectTo = `${window.location.origin}/reset-password`;
      const res = await fetch(`${supabaseFunctionsUrl}/generate-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ email: adminEmail, redirectTo }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResetResults((prev) => ({ ...prev, [adminEmail]: { ok: false, error: json.error || `Failed (${res.status})` } }));
        return;
      }
      const result = await sendPasswordResetEmail({ to_email: adminEmail, link: json.link });
      if (!result.ok && !result.skipped) {
        setResetResults((prev) => ({ ...prev, [adminEmail]: { ok: false, error: result.error || 'Failed to send email' } }));
      } else {
        setResetResults((prev) => ({ ...prev, [adminEmail]: { ok: true } }));
      }
    } catch (err) {
      setResetResults((prev) => ({ ...prev, [adminEmail]: { ok: false, error: err?.message || 'Failed' } }));
    } finally {
      setSendingResetFor(null);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters long' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (currentPassword === newPassword) {
      setMessage({ type: 'error', text: 'New password must be different from current password' });
      return;
    }

    setLoading(true);

    try {
      // Verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) {
        setMessage({ type: 'error', text: 'Current password is incorrect' });
        setLoading(false);
        return;
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setMessage({ type: 'error', text: updateError.message || 'Failed to update password' });
      } else {
        setMessage({ type: 'success', text: 'Password updated successfully!' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'An error occurred' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="settings">
        <div className="settings-header">
          <h1>Settings</h1>
          <p>Manage your account settings</p>
        </div>

        <div className="settings-container">
          <div className="settings-tabs">
            <button
              className={`settings-tab ${activeTab === 'reset-password' ? 'active' : ''}`}
              onClick={() => setActiveTab('reset-password')}
            >
              Reset Password
            </button>
            <button
              className={`settings-tab ${activeTab === 'admins' ? 'active' : ''}`}
              onClick={() => setActiveTab('admins')}
            >
              Admins
            </button>
          </div>

          <div className="settings-content">
            {activeTab === 'admins' && (
              <div className="settings-section">
                <h2>Admins</h2>
                <p className="settings-description">
                  View all admin accounts and send a password reset link to any admin.
                </p>
                {adminsLoading && <p className="settings-admins-loading">Loading admins...</p>}
                {adminsError && <div className="settings-message error">{adminsError}</div>}
                {!adminsLoading && !adminsError && admins.length === 0 && (
                  <p className="settings-admins-empty">No admin accounts found.</p>
                )}
                {!adminsLoading && admins.length > 0 && (
                  <ul className="settings-admins-list">
                    {admins.map((admin) => {
                      const result = resetResults[admin.email];
                      const isSending = sendingResetFor === admin.email;
                      const isCurrentUser = admin.user_id === user?.id;
                      return (
                        <li key={admin.user_id} className="settings-admin-row">
                          <span className="settings-admin-email">
                            {admin.email}
                            {isCurrentUser && <span className="settings-admin-you"> (you)</span>}
                          </span>
                          <div className="settings-admin-actions">
                            {result?.ok && (
                              <span className="settings-admin-sent">Reset link sent</span>
                            )}
                            {result && !result.ok && (
                              <span className="settings-admin-error">{result.error}</span>
                            )}
                            <button
                              className="settings-admin-reset-btn"
                              onClick={() => handleSendAdminReset(admin.email)}
                              disabled={isSending}
                            >
                              {isSending ? 'Sending...' : 'Send reset link'}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {activeTab === 'reset-password' && (
              <div className="settings-section">
                <h2>Reset Password</h2>
                <p className="settings-description">
                  Change your account password. Make sure to use a strong password.
                </p>

                <form onSubmit={handleResetPassword} className="settings-form">
                  <div className="form-group">
                    <label htmlFor="currentPassword">Current Password</label>
                    <input
                      type="password"
                      id="currentPassword"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter your current password"
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="newPassword">New Password</label>
                    <input
                      type="password"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter your new password (min. 6 characters)"
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm New Password</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your new password"
                      disabled={loading}
                    />
                  </div>

                  {message.text && (
                    <div className={`settings-message ${message.type}`}>
                      {message.text}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="settings-submit-btn"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Password'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;

